/** Whether a numeric grid column holds whole numbers or fractional values. */
export type NumberColumnKind = 'integer' | 'decimal';

/**
 * Coerce a raw numeric cell input to the value a column emits on write, based on
 * its kind:
 *   - `integer` → a JS number (any fractional part truncated), or null;
 *   - `decimal` → the string kept as-is (precision-safe — a JS number is
 *     IEEE-754 and would round), with a comma normalized to a dot.
 * Empty / blank input is null for both. Non-numeric integer input is null.
 *
 * A decimal stays a string so the backend's arbitrary-precision decimal
 * round-trips losslessly; an integer becomes a number because the backend's
 * integer JSON-unmarshal rejects a quoted string.
 */
export function coerceNumberCell(raw: string, kind: NumberColumnKind): number | string | null {
  const normalized = raw.replace(',', '.').trim();
  if (normalized === '') return null;
  if (kind === 'integer') {
    const n = Number(normalized);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  }
  return normalized;
}

const decimalSeparators = new Map<string, string>();

function decimalSeparatorOf(locale: string): string {
  let separator = decimalSeparators.get(locale);
  if (separator === undefined) {
    separator =
      new Intl.NumberFormat(locale).formatToParts(1.5).find((part) => part.type === 'decimal')
        ?.value ?? '.';
    decimalSeparators.set(locale, separator);
  }
  return separator;
}

/**
 * The text a numeric cell's editor starts with: the stored value in the user's
 * locale ("8,5" in German). The display cell already shows it that way, and
 * the editor must not switch to the wire's "8.5" the moment its row becomes
 * active (BLP/nx#2236). A string replacement, not Number(), so a decimal keeps
 * every digit (I-105); the write path turns the comma back into a dot.
 */
export function numberCellEditText(value: unknown, locale: string | undefined): string {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  if (!locale) return raw;
  const separator = decimalSeparatorOf(locale);
  return separator === '.' ? raw : raw.replace('.', separator);
}
